import React, { useState, useEffect } from "react";
import { getOwnerReport, explainOwner, enrichOwnerSignals } from "../../../api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../ui/dialog";
import { Building2, TrendingUp, Home, PoundSterling, Sparkles, Loader2, MapPin, Landmark, Satellite } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";

const money = (n) => (n || n === 0 ? `£${Number(n).toLocaleString()}` : "—");

const Prob = ({ label, value }) => (
  <div>
    <div className="flex justify-between text-[12px] mb-1"><span className="text-[#6a6a6a]">{label}</span><span className="font-semibold text-[#2b2b2b]">{Math.round((value || 0) * 100)}%</span></div>
    <div className="h-2 rounded-full bg-[#f0ece5] overflow-hidden"><div className="h-full bg-[#b8935f] rounded-full transition-all" style={{ width: `${Math.round((value || 0) * 100)}%` }} /></div>
  </div>
);

const OwnerIntelReport = ({ ownerId, open, onOpenChange, onActioned }) => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [reasoning, setReasoning] = useState("");
  const [enriching, setEnriching] = useState(false);

  useEffect(() => {
    if (!open || !ownerId) return;
    setData(null); setReasoning("");
    (async () => {
      setLoading(true);
      try {
        const d = await getOwnerReport(ownerId);
        setData(d);
        setReasoning(d.reasoning || "");
        if (!d.reasoning) { runExplain(); }
      } catch { toast({ title: "Couldn't load owner report", variant: "destructive" }); }
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [open, ownerId]);

  const runExplain = async () => {
    setExplaining(true);
    try { const r = await explainOwner(ownerId); setReasoning(r.reasoning); }
    catch { toast({ title: "AI explanation failed", variant: "destructive" }); }
    setExplaining(false);
  };

  const refreshSignals = async () => {
    setEnriching(true);
    try {
      const r = await enrichOwnerSignals(ownerId);
      const d = await getOwnerReport(ownerId);
      setData(d);
      const live = r.fields || [];
      toast({ title: live.length ? `Live signals updated (${live.join(", ")})` : "No live data matched", description: (r.liveSignals || {}).notes });
    } catch { toast({ title: "Couldn't fetch live signals", variant: "destructive" }); }
    setEnriching(false);
  };

  const isLive = (f) => (data?.liveFields || []).includes(f);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="owner-intel-report">
        <DialogTitle className="sr-only">Owner Intelligence report</DialogTitle>
        <DialogDescription className="sr-only">AI-generated owner intelligence, opportunity scores and recommended actions.</DialogDescription>
        {loading || !data ? (
          <div className="py-16 grid place-items-center text-[#8a8a8a]"><Loader2 className="animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-[#b8935f] font-medium flex items-center gap-1.5"><Landmark size={13} /> Owner Intelligence</div>
                <h2 className="text-[24px] font-semibold text-[#2b2b2b] mt-1" data-testid="owner-name">{data.owner}</h2>
                <div className="text-[13px] text-[#8a8a8a] flex items-center gap-3 mt-1 flex-wrap">
                  {data.company && <span className="inline-flex items-center gap-1"><Building2 size={13} /> {data.company}</span>}
                  <span className="inline-flex items-center gap-1"><MapPin size={13} /> {data.address}</span>
                </div>
              </div>
              <button onClick={refreshSignals} disabled={enriching} data-testid="refresh-live-signals" className="shrink-0 flex items-center gap-1.5 text-[12px] border border-[#f0ece5] rounded-lg px-3 py-2 text-[#6a6a6a] hover:bg-[#faf7f2] disabled:opacity-60">
                {enriching ? <Loader2 size={13} className="animate-spin" /> : <Satellite size={13} className="text-[#b8935f]" />} Live signals
              </button>
            </div>

            {/* AI reasoning */}
            <div className="bg-[#faf7f2] border border-[#f0ece5] rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[12px] font-medium text-[#b8935f] flex items-center gap-1.5"><Sparkles size={14} /> AI reasoning</div>
                <button onClick={runExplain} disabled={explaining} data-testid="owner-regenerate-explain" className="text-[11px] text-[#8a8a8a] hover:text-[#b8935f] flex items-center gap-1">{explaining ? <Loader2 size={12} className="animate-spin" /> : null} Regenerate</button>
              </div>
              <p className="text-[13px] text-[#4a4a4a] leading-relaxed" data-testid="owner-reasoning">{explaining && !reasoning ? "Generating explanation…" : (reasoning || "—")}</p>
            </div>

            {/* Estimates */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[["Est. portfolio", `${data.estPortfolio}`, Home, null], ["Portfolio value", money(data.estPortfolioValue), PoundSterling, "valueEach"], ["Monthly income", money(data.estMonthlyIncome), TrendingUp, "marketRent"], ["Total equity", money(data.estTotalEquity), Landmark, "valueEach"]].map(([l, v, Icon, key]) => (
                <div key={l} className="bg-white border border-[#f0ece5] rounded-xl p-3">
                  <Icon size={15} className="text-[#b8935f]" />
                  <div className="text-[17px] font-semibold text-[#2b2b2b] mt-1.5">{v}</div>
                  <div className="text-[11px] text-[#8a8a8a]">{l} {key && isLive(key) ? <span className="text-green-600 font-medium not-italic">● live</span> : <span className="italic">(est.)</span>}</div>
                </div>
              ))}
            </div>
            {(data.lastSoldDate || isLive("ownershipYears")) && (
              <div className="text-[12px] text-[#6a6a6a] -mt-2">
                Owned ~{data.ownershipYears} years {isLive("ownershipYears") ? <span className="text-green-600">● Land Registry{data.lastSoldDate ? `, last sold ${data.lastSoldDate}` : ""}</span> : <span className="italic">(est.)</span>}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {/* Landlord */}
              <div className="border border-[#f0ece5] rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between"><span className="text-[13px] font-medium text-[#2b2b2b]">Landlord opportunity</span><span className="text-[20px] font-bold text-[#b8935f]">{data.landlord.score}<span className="text-[12px] text-[#8a8a8a]">/100</span></span></div>
                <Prob label="Active landlord" value={data.landlord.pLandlord} />
                <Prob label="Needs management ≤12m" value={data.landlord.pNeedsManagement} />
                <div className="text-[12px] text-[#6a6a6a]">Est. annual mgmt revenue <span className="font-semibold text-[#2b2b2b]">{money(data.landlord.estAnnualMgmtRevenue)}</span></div>
                <div className="text-[12px] bg-[#faf7f2] rounded-lg px-3 py-2 text-[#4a4a4a]"><span className="text-[#b8935f] font-medium">Next:</span> {data.landlord.recommended.action}</div>
              </div>
              {/* Seller */}
              <div className="border border-[#f0ece5] rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between"><span className="text-[13px] font-medium text-[#2b2b2b]">Seller propensity</span><span className="text-[20px] font-bold text-[#b8935f]">{data.seller.score}<span className="text-[12px] text-[#8a8a8a]">/100</span></span></div>
                <Prob label="Lists ≤3 months" value={data.seller.p3m} />
                <Prob label="Lists ≤6 months" value={data.seller.p6m} />
                <Prob label="Lists ≤12 months" value={data.seller.p12m} />
                <div className="text-[12px] bg-[#faf7f2] rounded-lg px-3 py-2 text-[#4a4a4a]"><span className="text-[#b8935f] font-medium">Next:</span> {data.seller.recommended.action}</div>
              </div>
            </div>

            {/* Signals */}
            <div>
              <div className="text-[12px] font-medium text-[#6a6a6a] mb-2">Main signals</div>
              <div className="flex flex-wrap gap-2">
                {[...(data.landlord.signals || []), ...(data.seller.signals || [])].map((s, i) => (
                  <span key={i} className="text-[12px] bg-white border border-[#f0ece5] rounded-full px-3 py-1 text-[#4a4a4a]">{s}</span>
                ))}
              </div>
            </div>

            <div className="bg-[#2b2b2b] text-white rounded-xl px-4 py-3 flex items-center justify-between">
              <div><div className="text-[11px] text-[#b8935f] uppercase tracking-wide">Recommended service</div><div className="text-[15px] font-medium mt-0.5">{data.recommendedService}</div></div>
            </div>
            <p className="text-[11px] text-[#a8a8a8] italic">All financial figures are estimates derived from available signals and area data — not a guaranteed valuation.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OwnerIntelReport;
